/**
 * Plate Search Service - API ONLY
 * 
 * This service ONLY searches existing plates via the company API.
 * It does NOT generate or invent plate combinations.
 * All results come from the API.
 */

import apiClient from '@/services/api/apiClient'
import { 
  type VehicleCategory, 
  type PlateType,
  type PlateSize,
  type PlateRule,
  findRule,
  validatePlateInput,
  type PlateInput,
} from '@/services/rules/plateRulesEngine'
import { isValidCityCode } from '@/data/germanCityCodes'

export interface SearchCriteria {
  cityCode?: string
  letters?: string  // Can contain ? as wildcard
  numbers?: string  // Can contain ? as wildcard
  vehicleCategory?: VehicleCategory
  plateSize?: PlateSize
  plateType?: PlateType
  registrationOfficeServiceId?: number  // Required for API call
  seasonFrom?: number  // 1-12
  seasonTo?: number    // 1-12
}

export interface SearchResult {
  cityCode: string
  letters: string
  numbers: string
  plateSize: PlateSize
  plateType: PlateType
  vehicleCategory: VehicleCategory
  rule: PlateRule
}

/**
 * Map VehicleCategory to API vehicleType
 */
function mapVehicleCategoryToAPI(vehicleCategory: VehicleCategory): 'CAR' | 'MOTORCYCLE' {
  switch (vehicleCategory) {
    case 'PKW':
    case 'TRUCK':
    case 'TRAILER':
    case 'TRAKTOR':
      return 'CAR'
    case 'MOTORCYCLE':
    case 'LEICHTKRAFT':
      return 'MOTORCYCLE'
    default:
      return 'CAR'
  }
}

/**
 * Map PlateType to API licensePlateType
 */
function mapPlateTypeToAPI(plateType: PlateType): 
  'REGULAR' | 'REGULAR_SEASON' | 'ELECTRIC' | 'ELECTRIC_SEASON' | 'HISTORICAL' | 'HISTORICAL_SEASON' {
  switch (plateType) {
    case 'REGULAR':
      return 'REGULAR'
    case 'REGULAR_SEASON':
      return 'REGULAR_SEASON'
    case 'E':
      return 'ELECTRIC'
    case 'E_SEASON':
      return 'ELECTRIC_SEASON'
    case 'H':
      return 'HISTORICAL'
    case 'H_SEASON':
      return 'HISTORICAL_SEASON'
    default:
      return 'REGULAR'
  }
}

/**
 * Validate search criteria before API call
 */
export function validateSearchCriteria(
  criteria: SearchCriteria,
  vehicleCategory: VehicleCategory,
  plateSize: PlateSize,
  plateType: PlateType
): { valid: boolean; error?: string } {
  // At least one field must be filled
  if (!criteria.cityCode && !criteria.letters && !criteria.numbers) {
    return { valid: false, error: 'Bitte geben Sie mindestens ein Suchfeld ein' }
  }

  // Find rule for validation
  const rule = findRule(vehicleCategory, plateSize, plateType)
  if (!rule) {
    return { valid: false, error: 'Ungültige Kombination aus Fahrzeugtyp, Größe und Typ' }
  }

  // Validate city code if provided (without wildcards)
  if (criteria.cityCode && !criteria.cityCode.includes('?')) {
    if (!isValidCityCode(criteria.cityCode)) {
      return { valid: false, error: 'Ungültiger Stadtcode' }
    }
    
    const cityLength = criteria.cityCode.length
    if (cityLength < rule.minCityCodeLength || cityLength > rule.maxCityCodeLength) {
      return { valid: false, error: `Stadtcode muss zwischen ${rule.minCityCodeLength} und ${rule.maxCityCodeLength} Zeichen lang sein` }
    }
  }

  // Validate letters length if provided (without wildcards)
  if (criteria.letters && !criteria.letters.includes('?')) {
    if (criteria.letters.length > 2) {
      return { valid: false, error: 'Buchstabenblock darf maximal 2 Zeichen lang sein' }
    }
  }

  // Validate numbers length if provided (without wildcards)
  if (criteria.numbers && !criteria.numbers.includes('?')) {
    if (criteria.numbers.length > 4) {
      return { valid: false, error: 'Zahlenblock darf maximal 4 Ziffern lang sein' }
    }
  }

  // If all fields are provided without wildcards, validate the full combination
  if (criteria.cityCode && criteria.letters && criteria.numbers && 
      !criteria.cityCode.includes('?') && !criteria.letters.includes('?') && !criteria.numbers.includes('?')) {
    const plateInput: PlateInput = {
      cityCode: criteria.cityCode,
      letters: criteria.letters,
      numbers: criteria.numbers,
    }
    
    const validation = validatePlateInput(rule, plateInput)
    if (!validation.valid) {
      return { valid: false, error: validation.error || 'Ungültige Kombination' }
    }
  }

  // Check registrationOfficeServiceId
  if (!criteria.registrationOfficeServiceId) {
    return { valid: false, error: 'Zulassungsstelle muss ausgewählt werden' }
  }

  return { valid: true }
}

/**
 * Search for available license plates via API
 * 
 * This function ONLY sends requests to the API and returns results.
 * It does NOT generate or invent plate combinations.
 */
export async function searchPlates(
  criteria: SearchCriteria,
  onProgress?: (progress: number) => void
): Promise<SearchResult[]> {
  // Validate required fields
  if (!criteria.vehicleCategory || !criteria.plateSize || !criteria.plateType) {
    throw new Error('Fahrzeugtyp, Größe und Typ müssen ausgewählt sein')
  }

  // Validate search criteria
  const validation = validateSearchCriteria(
    criteria,
    criteria.vehicleCategory,
    criteria.plateSize,
    criteria.plateType
  )
  
  if (!validation.valid) {
    throw new Error(validation.error || 'Ungültige Suchkriterien')
  }

  // Map to API types
  const apiVehicleType = mapVehicleCategoryToAPI(criteria.vehicleCategory)
  const apiPlateType = mapPlateTypeToAPI(criteria.plateType)

  // Prepare API request
  // API requires all three fields (city, middle, end)
  // Use wildcards if field is empty
  const city = criteria.cityCode || '?'
  const middle = criteria.letters || '?'
  const end = criteria.numbers || '?'

  // If all are wildcards, that's not valid
  if (city === '?' && middle === '?' && end === '?') {
    throw new Error('Mindestens ein Feld muss ausgefüllt sein')
  }

  // Find rule for result mapping
  const rule = findRule(criteria.vehicleCategory, criteria.plateSize, criteria.plateType)
  if (!rule) {
    throw new Error('Ungültige Kombination aus Fahrzeugtyp, Größe und Typ')
  }

  // Show progress
  if (onProgress) {
    onProgress(10) // Starting
  }

  try {
    // Prepare request body
    const requestBody: any = {
      registrationOfficeServiceId: criteria.registrationOfficeServiceId,
      licensePlateNumberPatternComponents: {
        city: city,
        middle: middle,
        end: end,
      },
      licensePlateType: apiPlateType,
      vehicleType: apiVehicleType,
    }

    // Add season months if plate type is seasonal
    if (apiPlateType === 'REGULAR_SEASON' || apiPlateType === 'ELECTRIC_SEASON' || apiPlateType === 'HISTORICAL_SEASON') {
      requestBody.seasonStartMonth = criteria.seasonFrom || 4
      requestBody.seasonEndMonth = criteria.seasonTo || 10
    }

    // Log request for debugging
    console.log('Search API Request:', {
      url: '/licensePlateReservations/availabilityChecks',
      body: requestBody,
      vehicleType: apiVehicleType,
      plateType: apiPlateType,
    })

    // Show progress
    if (onProgress) {
      onProgress(50) // Request sent
    }

    // Call API
    const response = await apiClient.post('/licensePlateReservations/availabilityChecks', requestBody)
    
    // Log response for debugging
    console.log('Search API Response:', {
      status: response.status,
      data: response.data,
      availablePlates: response.data?.availableLicensePlateNumbers?.length || 0,
    })

    // Show progress
    if (onProgress) {
      onProgress(90) // Response received
    }

    // Parse response
    const availablePlates = response.data?.availableLicensePlateNumbers || []
    
    // Log parsed results
    console.log('Parsed available plates:', {
      count: availablePlates.length,
      firstPlate: availablePlates[0],
      allPlates: availablePlates,
    })

    // Convert API response to SearchResult[]
    const results: SearchResult[] = availablePlates.map((plate: any) => {
      // Handle different response formats
      const cityCode = plate.city || plate.cityCode || ''
      const letters = plate.middle || plate.letters || ''
      const numbers = plate.end || plate.numbers || ''
      
      return {
        cityCode,
        letters,
        numbers,
        plateSize: criteria.plateSize!,
        plateType: criteria.plateType!,
        vehicleCategory: criteria.vehicleCategory!,
        rule: rule,
      }
    })
    
    console.log('Converted results:', {
      count: results.length,
      firstResult: results[0],
    })

    // Show progress
    if (onProgress) {
      onProgress(100) // Complete
    }

    return results
  } catch (error: any) {
    console.error('API search error:', error)
    console.error('Error details:', {
      error,
      message: error.message,
      response: error.response,
      status: error.response?.status,
      data: error.response?.data,
      config: error.config,
    })
    
    // Handle different error types
    // apiClient interceptor returns ApiError directly (not error.response)
    if (error.error) {
      // This is an ApiError from apiClient interceptor
      const errorMessage = error.error === 'CORS_ERROR' 
        ? 'CORS-Fehler: Der Server erlaubt keine Anfragen von diesem Ursprung'
        : error.error === 'NETWORK_ERROR'
        ? 'Netzwerkfehler: Bitte überprüfen Sie Ihre Internetverbindung'
        : error.error
      
      throw new Error(errorMessage)
    }
    
    // Handle axios errors (if not intercepted)
    if (error.response) {
      const status = error.response.status
      const statusText = error.response.statusText
      const responseData = error.response.data
      
      if (status === 503) {
        // 503 Service Unavailable - Worker or API is down
        const detailMessage = responseData?.error || responseData?.message || 'Der Service ist derzeit nicht verfügbar'
        throw new Error(`Service nicht verfügbar (503): ${detailMessage}. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.`)
      }
      
      if (responseData?.error) {
        throw new Error(responseData.error)
      }
      
      if (status === 400) {
        const validationError = responseData?.message || responseData?.details || 'Ungültige Suchanfrage'
        throw new Error(`Ungültige Suchanfrage (400): ${validationError}. Bitte überprüfen Sie Ihre Eingaben.`)
      }
      
      if (status === 401) {
        throw new Error('Authentifizierungsfehler (401): Bitte kontaktieren Sie den Support.')
      }
      
      if (status === 404) {
        throw new Error('API-Endpunkt nicht gefunden (404). Bitte kontaktieren Sie den Support.')
      }
      
      throw new Error(`API-Fehler (${status}): ${statusText || responseData?.message || 'Unbekannter Fehler'}`)
    }
    
    // Handle network errors
    if (error.request && !error.response) {
      throw new Error('Netzwerkfehler: Keine Antwort vom Server. Bitte überprüfen Sie Ihre Internetverbindung oder kontaktieren Sie den Support.')
    }
    
    // Handle error messages
    if (error.message) {
      throw new Error(error.message)
    }
    
    // Fallback
    throw new Error('Fehler bei der Suche. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.')
  }
}
